import {
  Phases,
  VETERAN_GREETING_LEVEL,
  levelForXp,
  type ClientIntent,
  type NarrationEvent,
  type Phase,
  type PlayerPublic,
  type PrivateView,
  type PublicView,
  type RoleId,
  type ScoreAward,
  type ServerMessage,
} from '@ringmaster/shared';
import type { ConnectOptions, GameDriver } from './GameDriver.js';
import { HOST_ID, MOCK_CODE, MOCK_PLAYERS } from './mockFixtures.js';

/**
 * Lightweight caption text for the mock's scripted beats. The real narration
 * package owns the full theatrical stock-line map; here we only need plausible
 * captions to exercise the UI, so a compact inline map keeps the client free of
 * a narration dependency.
 */
function mockNarrationText(event: NarrationEvent): string {
  switch (event.type) {
    case 'INTRO':
      // The progression payoff (issue #6): the table's veteran is welcomed back
      // by name before the deal. Absent — every table below the threshold — the
      // opening is unchanged.
      return event.veteranName && event.veteranLevel != null
        ? `Welcome back, ${event.veteranName} — level ${event.veteranLevel}. ${event.playerNames.length} souls gather in ${event.theme}; one wears a stolen face.`
        : `${event.playerNames.length} souls gather in ${event.theme}. One wears a stolen face — learn your fate.`;
    case 'NIGHTFALL':
      return 'Night falls on Ravenhollow. Everyone, close your eyes.';
    case 'WOLVES_WAKE':
      return 'Werewolves, awaken. Choose your prey.';
    case 'WOLVES_SLEEP':
      return 'Werewolves, close your eyes. Your hunt is over — sleep now.';
    case 'SEER_WAKE':
      return 'Seer, awaken. Seek the truth in one face.';
    case 'DAWN_DEATH':
      return event.victimName
        ? `Dawn breaks. ${event.victimName} lies cold — a ${event.role?.toLowerCase() ?? 'soul'}.`
        : 'Dawn breaks, and no one fell in the night.';
    case 'DISCUSS_START':
      return `You have ${Math.round(event.secs / 60)} minutes. Find the wolf among you.`;
    case 'TIME_WARNING':
      return `${event.secondsLeft} seconds remain.`;
    case 'VOTE_CALL':
      return 'Time. Cast your votes — who hangs at dusk?';
    case 'LAST_WORDS':
      return `${event.targetName}, any last words before the rope?`;
    case 'LYNCH':
      return event.tie
        ? 'The village cannot agree. No one hangs tonight.'
        : `${event.targetName}, the rope takes you — you were a ${event.role?.toLowerCase() ?? 'soul'}.`;
    case 'WIN':
      return event.winner === 'VILLAGE'
        ? 'Ravenhollow is safe. The village triumphs!'
        : 'The wolves have won Ravenhollow.';
    // ── The Whisper — placeholder mock captions (the mock scripts Blood Moon;
    //    these keep the exhaustive switch total). TODO(whisper M6).
    case 'WHISPER_WELCOME':
      return 'Come closer, seekers. The séance begins.';
    case 'WHISPER_INTRO':
      return `The Seer holds a word — ${event.categoryHint}. Find it before the candle dies.`;
    case 'WHISPER_HINT':
      return `A hint, seekers: ${event.hint}`;
    case 'WHISPER_HOLD':
      return `A guardian stays ${event.targetName}'s hand.`;
    case 'WHISPER_ANSWER':
      return event.heat === 'warm' ? 'Closer… you feel the heat.' : 'No… not that.';
    case 'WHISPER_QUESTION':
      return event.answer ?? 'The Seer is silent on that.';
    case 'WORD_FOUND':
      return `${event.finderName} has found the word.`;
    case 'CANDLE_DIES':
      return 'The candle dies. The word is lost.';
    case 'HUNT_CALL':
      return 'Point to the one who deceived you.';
    case 'ROUND_SCORE':
      return `Round ${event.roundIndex + 1} of ${event.totalRounds}.`;
    // ── Blood Moon: One Night — placeholder mock captions (the mock scripts
    //    Blood Moon proper; these keep the exhaustive switch total).
    //    TODO(onenight): a real scripted One Night mock.
    case 'ONENIGHT_NIGHTFALL':
      return 'One night falls on Ravenhollow. Everyone, close your eyes.';
    case 'ONENIGHT_NIGHT_ACTIONS':
      return 'Werewolves, Seer and Thief awaken together — know each other, seek a truth, take another’s fate.';
    case 'ONENIGHT_JOKER':
      return 'Joker, awaken. Twist two fates, blind.';
    case 'ONENIGHT_DAWN':
      return 'Dawn breaks. Open your eyes — the swapping is done.';
    case 'ONENIGHT_RESOLVE':
      return event.victims.length > 0
        ? `${event.victims.map((v) => v.name).join(', ')} did not survive the vote.`
        : 'The village cannot agree. No one dies tonight.';
    case 'NEED_PLAYERS':
      return 'Too few souls at this table. Fetch me more before we begin.';
    default: {
      const _exhaustive: never = event;
      void _exhaustive;
      return 'The tale continues...';
    }
  }
}

/**
 * MockDriver — a WORKING in-memory implementation of GameDriver.
 *
 * It scripts the full BLOOD_MOON_FLOW.md game with the 6-player table
 * (Ana host/Villager, Ben Wolf, Cara Seer, Dan/Eve/Finn Villagers) and walks the
 * ENTIRE phase sequence to a Village win. It does NOT run the real engine —
 * every state snapshot is hand-authored so UI agents can build against a live,
 * deterministic driver with no server.
 *
 * Advancing: `HOST_ADVANCE` (and player actions like READY / SUBMIT_VOTE that
 * complete a phase) move the script forward. A convenience `advance()` is also
 * exposed for smoke tests / storybook stepping.
 */

interface ScriptStep {
  public: PublicView;
  /** Private view per player id. */
  privates: Record<string, PrivateView>;
  /** Narration beat emitted on entering this step (Stage gets audioId, Hands null). */
  narration?: NarrationEvent;
  /** Timer to arm on entering this step. */
  timer?: { phase: Phase; seconds: number };
}

const THEME = 'Ravenhollow';
const ROLE_MAP: Record<string, RoleId> = Object.fromEntries(
  MOCK_PLAYERS.map((p) => [p.id, p.role]),
);
const NAME: Record<string, string> = Object.fromEntries(
  MOCK_PLAYERS.map((p) => [p.id, p.name]),
);

function team(role: RoleId): 'VILLAGE' | 'WOLVES' {
  return role === 'WEREWOLF' ? 'WOLVES' : 'VILLAGE';
}

function roster(dead: string[], reveal = false): PlayerPublic[] {
  const deadSet = new Set(dead);
  return MOCK_PLAYERS.map((p) => ({
    id: p.id,
    name: p.name,
    alive: !deadSet.has(p.id),
    connected: true,
    revealedRole: deadSet.has(p.id) || reveal ? p.role : null,
    score: 0,
    // Lifetime progression, same derivation as mockFixtures' roster: `level` is
    // a pure function of `xp` (shared/levels.ts), so the badge a mock surface
    // draws is the one the real curve would draw. Finn carries no XP — the
    // "no badge until your first game" case.
    xp: p.xp,
    level: p.xp == null ? null : levelForXp(p.xp),
  }));
}

/**
 * The greeting the SERVER would patch onto this roster's INTRO beat (issue #6):
 * the highest-level seat, once it has reached `VETERAN_GREETING_LEVEL`. Derived
 * from the mock roster rather than hand-typed, so a change to the fixture XP
 * moves the mock's greeting exactly as it would move a real table's.
 */
function mockVeteran(): { veteranName?: string; veteranLevel?: number } {
  let best: { name: string; level: number } | null = null;
  for (const p of MOCK_PLAYERS) {
    if (p.xp == null) continue;
    const level = levelForXp(p.xp);
    if (level < VETERAN_GREETING_LEVEL) continue;
    if (!best || level > best.level) best = { name: p.name, level };
  }
  return best ? { veteranName: best.name, veteranLevel: best.level } : {};
}

/**
 * The scripted ending's ledger, itemised for every seat exactly as the server
 * sends it (`PublicView.awards`). Matches the script: the village wins, Cara
 * (the mock's own seat) is the Seer who lived and read Ben correctly, Ben was
 * the wolf who took the Seer's neighbour in the night, and three villagers died
 * for it. Every bonus total is inside BONUS_CAP.
 */
const MOCK_AWARDS: ScoreAward[] = [
  { playerId: 'p_cara', code: 'WIN', points: 200, gameId: 'bloodmoon' },
  { playerId: 'p_cara', code: 'ORACLE', points: 80, gameId: 'bloodmoon' },
  { playerId: 'p_cara', code: 'SEER_MARK', points: 20, gameId: 'bloodmoon' },
  { playerId: 'p_cara', code: 'SCENT', points: 30, gameId: 'bloodmoon' },
  { playerId: 'p_finn', code: 'WIN', points: 200, gameId: 'bloodmoon' },
  { playerId: 'p_finn', code: 'SCENT', points: 30, gameId: 'bloodmoon' },
  { playerId: 'p_ana', code: 'WIN', points: 200, gameId: 'bloodmoon' },
  { playerId: 'p_ana', code: 'MARTYR', points: 30, gameId: 'bloodmoon' },
  { playerId: 'p_dan', code: 'WIN', points: 200, gameId: 'bloodmoon' },
  { playerId: 'p_dan', code: 'MARTYR', points: 30, gameId: 'bloodmoon' },
  { playerId: 'p_eve', code: 'WIN', points: 200, gameId: 'bloodmoon' },
  { playerId: 'p_eve', code: 'MARTYR', points: 30, gameId: 'bloodmoon' },
  { playerId: 'p_ben', code: 'CLEAN_KILL', points: 30, gameId: 'bloodmoon' },
];

function pub(phase: Phase, round: number, over: Partial<PublicView>): PublicView {
  return {
    code: MOCK_CODE,
    phase,
    round,
    players: roster(over.players ? [] : []),
    config: { discussionSecs: 180 },
    minPlayers: 5,
    hostId: HOST_ID,
    wakingRole: null,
    dawn: null,
    voteProgress: null,
    skipVotes: null,
    lynchResult: null,
    result: null,
    // END-only (shared/scoring.ts). Every step but the last leaves it null; the
    // scripted ending patches in a full itemised ledger so `?mock=1` can walk
    // the score reveal with no server.
    awards: null,
    timerEndsAt: null,
    paused: false,
    timerRemainingMs: null,
    ...over,
  };
}

function priv(playerId: string, over: Partial<PrivateView>): PrivateView {
  const role = ROLE_MAP[playerId]!;
  return {
    playerId,
    role,
    team: team(role),
    alive: true,
    packmates: null,
    packAgreement: null,
    action: { type: 'NONE' },
    seerResult: null,
    seerHistory: null,
    deadTruth: null,
    ...over,
  };
}

/** Build the private map for a step: default view for everyone, then overrides. */
function privates(
  dead: string[],
  reveal: boolean,
  overrides: Record<string, Partial<PrivateView>>,
): Record<string, PrivateView> {
  const out: Record<string, PrivateView> = {};
  for (const p of MOCK_PLAYERS) {
    const isDead = dead.includes(p.id);
    const base: Partial<PrivateView> = {
      alive: !isDead,
      // The full roster is only revealed at END (the `reveal` flag). The dead do
      // NOT learn everyone's roles mid-game — matches engine views.ts gating.
      deadTruth: reveal ? { ...ROLE_MAP } : null,
    };
    out[p.id] = priv(p.id, { ...base, ...(overrides[p.id] ?? {}) });
  }
  return out;
}

/**
 * The scripted game. Each entry is one phase snapshot; ADVANCE walks the array.
 * Mirrors the FLOW.md play-by-play (Eve killed N1, Dan lynched D1, Ana killed N2,
 * Ben lynched D2 → Village wins).
 */
function buildScript(): ScriptStep[] {
  const allNames = MOCK_PLAYERS.map((p) => p.name);
  const steps: ScriptStep[] = [];

  // 0 — LOBBY
  steps.push({
    public: pub('LOBBY', 0, { players: roster([]) }),
    privates: privates([], false, {}),
  });

  // 1 — DEAL (reveal + ready)
  steps.push({
    public: pub('DEAL', 1, { players: roster([]), timerEndsAt: null }),
    privates: privates([], false, {
      p_ben: { action: { type: 'READY', done: false } },
      p_cara: { action: { type: 'READY', done: false } },
      p_ana: { action: { type: 'READY', done: false } },
      p_dan: { action: { type: 'READY', done: false } },
      p_eve: { action: { type: 'READY', done: false } },
      p_finn: { action: { type: 'READY', done: false } },
    }),
    narration: { type: 'INTRO', playerNames: allNames, theme: THEME, ...mockVeteran() },
    timer: { phase: Phases.DEAL, seconds: 30 },
  });

  // 2 — NIGHT_INTRO (ambient nightfall pause; everyone sleeps)
  steps.push({
    public: pub('NIGHT_INTRO', 1, { players: roster([]) }),
    privates: privates([], false, {}),
    narration: { type: 'NIGHTFALL', round: 1 },
    timer: { phase: Phases.NIGHT_INTRO, seconds: 10 },
  });

  // 3 — NIGHT_WOLVES (Ben picks Eve)
  steps.push({
    public: pub('NIGHT_WOLVES', 1, { players: roster([]), wakingRole: 'WEREWOLF' }),
    privates: privates([], false, {
      p_ben: {
        action: { type: 'WOLF_PICK', candidates: ['p_ana', 'p_cara', 'p_dan', 'p_eve', 'p_finn'], myPick: 'p_eve' },
      },
    }),
    narration: { type: 'WOLVES_WAKE' },
    timer: { phase: Phases.NIGHT_WOLVES, seconds: 40 },
  });

  // 4 — NIGHT_INTERLUDE (wolves close their eyes — "go to sleep" pause)
  steps.push({
    public: pub('NIGHT_INTERLUDE', 1, { players: roster([]) }),
    privates: privates([], false, {}),
    narration: { type: 'WOLVES_SLEEP' },
    timer: { phase: Phases.NIGHT_INTERLUDE, seconds: 8 },
  });

  // 5 — NIGHT_SEER (Cara investigates Finn → clean); the seer wakes here.
  steps.push({
    public: pub('NIGHT_SEER', 1, { players: roster([]), wakingRole: 'SEER' }),
    privates: privates([], false, {
      p_cara: {
        action: { type: 'SEER_PICK', candidates: ['p_ana', 'p_ben', 'p_dan', 'p_eve', 'p_finn'] },
      },
    }),
    narration: { type: 'SEER_WAKE' },
    timer: { phase: Phases.NIGHT_SEER, seconds: 30 },
  });

  // 4 — RESOLVE_NIGHT
  steps.push({
    public: pub('RESOLVE_NIGHT', 1, { players: roster([]) }),
    privates: privates([], false, {}),
  });

  // 5 — DAWN (Eve dead, Villager). Cara now holds her N1 result.
  steps.push({
    public: pub('DAWN', 1, { players: roster(['p_eve']), dawn: { victimId: 'p_eve' } }),
    privates: privates(['p_eve'], false, {
      p_cara: {
        seerResult: { targetId: 'p_finn', targetName: 'Finn', isWerewolf: false },
        seerHistory: [{ round: 1, targetId: 'p_finn', targetName: 'Finn', isWerewolf: false }],
      },
    }),
    narration: { type: 'DAWN_DEATH', victimName: 'Eve', role: 'VILLAGER', round: 1, theme: THEME },
  });

  // 6 — DISCUSS (5 alive)
  steps.push({
    public: pub('DISCUSS', 1, { players: roster(['p_eve']), timerEndsAt: null }),
    privates: privates(['p_eve'], false, {
      p_cara: {
        seerHistory: [{ round: 1, targetId: 'p_finn', targetName: 'Finn', isWerewolf: false }],
      },
    }),
    narration: {
      type: 'DISCUSS_START',
      secs: 180,
      aliveNames: ['Ana', 'Ben', 'Cara', 'Dan', 'Finn'],
    },
    timer: { phase: Phases.DISCUSS, seconds: 180 },
  });

  // 7 — VOTE (Dan gets lynched: 3 votes)
  steps.push({
    public: pub('VOTE', 1, {
      players: roster(['p_eve']),
      voteProgress: { voted: 5, total: 5 },
    }),
    privates: privates(['p_eve'], false, {
      p_ana: { action: { type: 'VOTE', candidates: ['p_ana', 'p_ben', 'p_cara', 'p_dan', 'p_finn'], myVote: 'p_dan' } },
      p_ben: { action: { type: 'VOTE', candidates: ['p_ana', 'p_ben', 'p_cara', 'p_dan', 'p_finn'], myVote: 'p_dan' } },
      p_cara: {
        action: { type: 'VOTE', candidates: ['p_ana', 'p_ben', 'p_cara', 'p_dan', 'p_finn'], myVote: 'p_ana' },
        seerHistory: [{ round: 1, targetId: 'p_finn', targetName: 'Finn', isWerewolf: false }],
      },
      p_dan: { action: { type: 'VOTE', candidates: ['p_ana', 'p_ben', 'p_cara', 'p_dan', 'p_finn'], myVote: 'p_ana' } },
      p_finn: { action: { type: 'VOTE', candidates: ['p_ana', 'p_ben', 'p_cara', 'p_dan', 'p_finn'], myVote: 'p_dan' } },
    }),
    narration: { type: 'VOTE_CALL' },
    timer: { phase: Phases.VOTE, seconds: 30 },
  });

  // 8 — RESOLVE_VOTE (Dan lynched, Villager). Per-voter tally revealed.
  steps.push({
    public: pub('RESOLVE_VOTE', 1, {
      players: roster(['p_eve', 'p_dan']),
      lynchResult: {
        targetId: 'p_dan',
        tie: false,
        votes: { p_ana: 'p_dan', p_ben: 'p_dan', p_cara: 'p_ana', p_dan: 'p_ana', p_finn: 'p_dan' },
      },
    }),
    privates: privates(['p_eve', 'p_dan'], false, {
      p_cara: {
        seerHistory: [{ round: 1, targetId: 'p_finn', targetName: 'Finn', isWerewolf: false }],
      },
    }),
    narration: {
      type: 'LYNCH',
      targetName: 'Dan',
      role: 'VILLAGER',
      tie: false,
      voteTally: { p_ana: 'p_dan', p_ben: 'p_dan', p_cara: 'p_ana', p_dan: 'p_ana', p_finn: 'p_dan' },
    },
  });

  // 9 — NIGHT_INTRO round 2 (ambient nightfall pause)
  steps.push({
    public: pub('NIGHT_INTRO', 2, { players: roster(['p_eve', 'p_dan']) }),
    privates: privates(['p_eve', 'p_dan'], false, {
      p_cara: {
        seerHistory: [{ round: 1, targetId: 'p_finn', targetName: 'Finn', isWerewolf: false }],
      },
    }),
    narration: { type: 'NIGHTFALL', round: 2 },
    timer: { phase: Phases.NIGHT_INTRO, seconds: 10 },
  });

  // 10 — NIGHT_WOLVES round 2 (Ben picks Ana)
  steps.push({
    public: pub('NIGHT_WOLVES', 2, { players: roster(['p_eve', 'p_dan']), wakingRole: 'WEREWOLF' }),
    privates: privates(['p_eve', 'p_dan'], false, {
      p_ben: {
        action: { type: 'WOLF_PICK', candidates: ['p_ana', 'p_cara', 'p_finn'], myPick: 'p_ana' },
      },
      p_cara: {
        seerHistory: [{ round: 1, targetId: 'p_finn', targetName: 'Finn', isWerewolf: false }],
      },
    }),
    narration: { type: 'WOLVES_WAKE' },
    timer: { phase: Phases.NIGHT_WOLVES, seconds: 40 },
  });

  // 11 — NIGHT_INTERLUDE round 2 (wolves close their eyes — "go to sleep" pause)
  steps.push({
    public: pub('NIGHT_INTERLUDE', 2, { players: roster(['p_eve', 'p_dan']) }),
    privates: privates(['p_eve', 'p_dan'], false, {
      p_cara: {
        seerHistory: [{ round: 1, targetId: 'p_finn', targetName: 'Finn', isWerewolf: false }],
      },
    }),
    narration: { type: 'WOLVES_SLEEP' },
    timer: { phase: Phases.NIGHT_INTERLUDE, seconds: 8 },
  });

  // 12 — NIGHT_SEER round 2 (Cara investigates Ben → WEREWOLF); the seer wakes here.
  steps.push({
    public: pub('NIGHT_SEER', 2, { players: roster(['p_eve', 'p_dan']), wakingRole: 'SEER' }),
    privates: privates(['p_eve', 'p_dan'], false, {
      p_cara: {
        action: { type: 'SEER_PICK', candidates: ['p_ana', 'p_ben', 'p_finn'] },
        seerHistory: [{ round: 1, targetId: 'p_finn', targetName: 'Finn', isWerewolf: false }],
      },
    }),
    narration: { type: 'SEER_WAKE' },
    timer: { phase: Phases.NIGHT_SEER, seconds: 30 },
  });

  // 11 — RESOLVE_NIGHT round 2
  steps.push({
    public: pub('RESOLVE_NIGHT', 2, { players: roster(['p_eve', 'p_dan']) }),
    privates: privates(['p_eve', 'p_dan'], false, {}),
  });

  // 12 — DAWN round 2 (Ana dead, Villager). 3 remain.
  steps.push({
    public: pub('DAWN', 2, { players: roster(['p_eve', 'p_dan', 'p_ana']), dawn: { victimId: 'p_ana' } }),
    privates: privates(['p_eve', 'p_dan', 'p_ana'], false, {
      p_cara: {
        seerResult: { targetId: 'p_ben', targetName: 'Ben', isWerewolf: true },
        seerHistory: [
          { round: 1, targetId: 'p_finn', targetName: 'Finn', isWerewolf: false },
          { round: 2, targetId: 'p_ben', targetName: 'Ben', isWerewolf: true },
        ],
      },
    }),
    narration: { type: 'DAWN_DEATH', victimName: 'Ana', role: 'VILLAGER', round: 2, theme: THEME },
  });

  // 13 — DISCUSS round 2 (3 alive: Ben, Cara, Finn)
  steps.push({
    public: pub('DISCUSS', 2, { players: roster(['p_eve', 'p_dan', 'p_ana']) }),
    privates: privates(['p_eve', 'p_dan', 'p_ana'], false, {
      p_cara: {
        seerHistory: [
          { round: 1, targetId: 'p_finn', targetName: 'Finn', isWerewolf: false },
          { round: 2, targetId: 'p_ben', targetName: 'Ben', isWerewolf: true },
        ],
      },
    }),
    narration: { type: 'DISCUSS_START', secs: 180, aliveNames: ['Ben', 'Cara', 'Finn'] },
    timer: { phase: Phases.DISCUSS, seconds: 180 },
  });

  // 14 — VOTE round 2 (Ben lynched: Cara + Finn vote Ben)
  steps.push({
    public: pub('VOTE', 2, {
      players: roster(['p_eve', 'p_dan', 'p_ana']),
      voteProgress: { voted: 3, total: 3 },
    }),
    privates: privates(['p_eve', 'p_dan', 'p_ana'], false, {
      p_ben: { action: { type: 'VOTE', candidates: ['p_ben', 'p_cara', 'p_finn'], myVote: 'p_cara' } },
      p_cara: {
        action: { type: 'VOTE', candidates: ['p_ben', 'p_cara', 'p_finn'], myVote: 'p_ben' },
        seerHistory: [
          { round: 1, targetId: 'p_finn', targetName: 'Finn', isWerewolf: false },
          { round: 2, targetId: 'p_ben', targetName: 'Ben', isWerewolf: true },
        ],
      },
      p_finn: { action: { type: 'VOTE', candidates: ['p_ben', 'p_cara', 'p_finn'], myVote: 'p_ben' } },
    }),
    narration: { type: 'VOTE_CALL' },
    timer: { phase: Phases.VOTE, seconds: 30 },
  });

  // 15 — RESOLVE_VOTE round 2 (Ben lynched, WEREWOLF → Village wins)
  steps.push({
    public: pub('RESOLVE_VOTE', 2, {
      players: roster(['p_eve', 'p_dan', 'p_ana', 'p_ben']),
      lynchResult: {
        targetId: 'p_ben',
        tie: false,
        votes: { p_ben: 'p_cara', p_cara: 'p_ben', p_finn: 'p_ben' },
      },
    }),
    privates: privates(['p_eve', 'p_dan', 'p_ana', 'p_ben'], false, {
      p_cara: {
        seerHistory: [
          { round: 1, targetId: 'p_finn', targetName: 'Finn', isWerewolf: false },
          { round: 2, targetId: 'p_ben', targetName: 'Ben', isWerewolf: true },
        ],
      },
    }),
    narration: {
      type: 'LYNCH',
      targetName: 'Ben',
      role: 'WEREWOLF',
      tie: false,
      voteTally: { p_ben: 'p_cara', p_cara: 'p_ben', p_finn: 'p_ben' },
    },
  });

  // 16 — END (Village wins, full reveal + the scored ledger)
  steps.push({
    public: pub('END', 2, {
      players: roster(['p_eve', 'p_dan', 'p_ana', 'p_ben'], true),
      result: { winner: 'VILLAGE', atDawn: false, roles: { ...ROLE_MAP } },
      awards: MOCK_AWARDS,
    }),
    privates: privates(['p_eve', 'p_dan', 'p_ana', 'p_ben'], true, {
      p_cara: {
        seerHistory: [
          { round: 1, targetId: 'p_finn', targetName: 'Finn', isWerewolf: false },
          { round: 2, targetId: 'p_ben', targetName: 'Ben', isWerewolf: true },
        ],
      },
    }),
    narration: { type: 'WIN', winner: 'VILLAGE', atDawn: false, theme: THEME },
  });

  return steps;
}

let audioSeq = 0;

export class MockDriver implements GameDriver {
  private subscribers = new Set<(msg: ServerMessage) => void>();
  private script: ScriptStep[] = buildScript();
  private index = 0;
  private opts: ConnectOptions = { role: 'stage' };
  /** Which player this driver represents when role === 'hand'. Defaults to Cara (Seer). */
  private selfId: string;

  constructor(selfId: string = 'p_cara') {
    this.selfId = selfId;
  }

  connect(opts: ConnectOptions): void {
    this.opts = opts;
    if (opts.role === 'hand') {
      // JOINED handshake for a hand.
      this.emit({
        type: 'JOINED',
        playerId: this.selfId,
        sessionToken: `mock-${this.selfId}`,
        code: MOCK_CODE,
        isHost: this.selfId === HOST_ID,
      });
    }
    this.pushCurrent(false);
  }

  send(intent: ClientIntent): void {
    switch (intent.type) {
      case 'CREATE_ROOM': {
        this.emit({
          type: 'JOINED',
          playerId: HOST_ID,
          sessionToken: `mock-${HOST_ID}`,
          code: MOCK_CODE,
          isHost: true,
        });
        this.pushCurrent(false);
        return;
      }
      case 'JOIN_ROOM':
      case 'REJOIN': {
        this.emit({
          type: 'JOINED',
          playerId: this.selfId,
          sessionToken: `mock-${this.selfId}`,
          code: MOCK_CODE,
          isHost: this.selfId === HOST_ID,
        });
        this.pushCurrent(false);
        return;
      }
      // Any intent that completes/advances a phase steps the script forward.
      case 'START_GAME':
      case 'READY':
      case 'SUBMIT_NIGHT_TARGET':
      case 'SUBMIT_INVESTIGATE':
      case 'SUBMIT_VOTE':
      case 'HOST_ADVANCE':
        this.advance();
        return;
      case 'PLAY_AGAIN':
        // Back to LOBBY, roster kept — same as the server. The END screen's
        // "Play again" follows this with START_GAME, which advances to DEAL;
        // "Change game" sends PLAY_AGAIN alone and stays in the lobby.
        this.index = 0;
        this.pushCurrent(true);
        return;
      case 'WATCH':
        // A spectator attaches to the (scripted) room — the mock treats it like
        // a stage claim: no script change, just re-emit the current public state.
        this.pushCurrent(false);
        return;
      case 'SET_CONFIG':
      case 'CLAIM_STAGE':
      case 'HOST_SET_PAUSED':
      case 'SET_NARRATOR_ONLY':
      case 'KICK_PLAYER':
      // ADD_AI_SEAT — inert: the mock's roster is scripted, and AI seats are a
      // real-server facility (they need the engine + a per-room controller).
      case 'ADD_AI_SEAT':
      // RESET_SCORES — the room's cumulative ledger lives on the SERVER; the
      // scripted mock has no such totals to wipe, so it is inert here.
      case 'RESET_SCORES':
      // SWITCH_GAME — inert in the (scripted, single-game) mock. A real
      // multi-game mock picker is a future TODO, same as the Whisper script.
      case 'SWITCH_GAME':
        // No script change; re-emit current state (mock has no real timers).
        this.pushCurrent(false);
        return;
      // ── The Whisper intents — inert in the (Blood Moon) mock script. A real
      //    scripted Whisper path arrives in M6. TODO(whisper M6).
      case 'SUBMIT_WHISPER':
      case 'PALADIN_HOLD':
      case 'EMOTE':
        this.pushCurrent(false);
        return;
      // ── One Night intents — inert in the (Blood Moon) mock script. A real
      //    scripted One Night path is a future TODO, same as the Whisper.
      case 'ONENIGHT_PEEK':
      case 'ONENIGHT_SWAP':
        this.pushCurrent(false);
        return;
      default: {
        const _exhaustive: never = intent;
        void _exhaustive;
        return;
      }
    }
  }

  onMessage(cb: (msg: ServerMessage) => void): () => void {
    this.subscribers.add(cb);
    return () => this.subscribers.delete(cb);
  }

  close(): void {
    this.subscribers.clear();
  }

  /** Public helper: advance the script one step (idempotent at END). */
  advance(): void {
    if (this.index < this.script.length - 1) {
      this.index += 1;
    }
    this.pushCurrent(true);
  }

  /** Current phase (handy for smoke tests). */
  get phase(): Phase {
    return this.script[this.index]!.public.phase;
  }

  private pushCurrent(withBeats: boolean): void {
    const step = this.script[this.index]!;
    // Narration first (so captions land before the state paints), Stage-only audio.
    if (withBeats && step.narration) {
      const isStage = this.opts.role === 'stage';
      this.emit({
        type: 'NARRATION',
        audioId: isStage ? `mock-audio-${audioSeq++}` : null,
        text: mockNarrationText(step.narration),
        captionOnly: !isStage,
      });
    }
    // Public state to everyone.
    this.emit({ type: 'PUBLIC_STATE', view: step.public });
    // Private state only for a hand.
    if (this.opts.role === 'hand') {
      const view = step.privates[this.selfId];
      if (view) this.emit({ type: 'PRIVATE_STATE', view });
    }
    // Timer if this step arms one.
    if (withBeats && step.timer) {
      this.emit({
        type: 'TIMER',
        phase: step.timer.phase,
        endsAt: Date.now() + step.timer.seconds * 1000,
      });
    }
  }

  private emit(msg: ServerMessage): void {
    for (const cb of this.subscribers) cb(msg);
  }
}
